import { faFolders, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { Location } from "history";
import React from "react";

import AboutProMe from "@/mk2/apps/blogs/components/AboutProMe";
import AddProductsBanner from "@/mk2/apps/blogs/components/AddProductsBanner";
import AvatarImgGrid from "@/mk2/apps/blogs/components/AvatarImgGrid";
import { MyStrollerProduct } from "@/mk2/apps/blogs/components/MyProduct";
import UserGroupsList from "@/mk2/apps/blogs/components/UserGroupsList";
import { ProfileDetailData } from "@/mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.reducers";
import { blogsProfileProductsUrl } from "@/mk2/apps/blogs/urls";
import { dummyPhoto } from "@/mk2/apps/strollers/constants";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { cacheLast } from "@/mk2/helpers/cache";
import { url } from "@/mk2/helpers/urls";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { ProfileProduct, ProfileProductType, UserEntity } from "@/mk2/schemas";
import {
    FAVORITE_USERS,
    HAVE_ME_AS_FAVORITE,
    I_AM_MEMBER_OF_GROUPS,
    MORE_ABOUT_ME_MY_PRODUCTS,
    MORE_ABOUT_ME_MY_PRODUCTS_ADD_FIRST_PRODUCT,
    MORE_ABOUT_ME_MY_PRODUCTS_SHOW,
} from "@/mk/autogenerated/translations/MoreAboutMe.58941a9753c7026c4df95a52463cc11d";
import { canManageBlogs, isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

import styles from "./MoreAboutMe.mscss";

export enum AvatarGridType {
    FOLLOWERS = "followers",
    FOLLOWING = "following",
}

interface OwnProps {
    isMobile: boolean;
    location: Location;
    profileDetailData: ProfileDetailData;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    onExpandAvatarGrid(avatarGridType: AvatarGridType, username: string);
    onExpandGroupList(username: string);
}

type Props = OwnProps;

export default class MoreAboutMe extends React.Component<Props> {
    private onExpandAvatarGridCache = cacheLast();
    private onExpandGroupListCache = cacheLast();
    private profileProducts = cacheLast<ProfileProduct[]>(true);

    public render() {
        const {
            isMobile,
            profileDetailData,
            requestUser,
            requestUserPermissions,
            onExpandAvatarGrid,
            onExpandGroupList,
            location,
        } = this.props;

        const isBlogsAdmin = canManageBlogs(requestUserPermissions);
        const isOwner = isAuthenticatedAs(requestUser, profileDetailData.user);

        const {
            followers,
            following,
            memberOfGroups,
            pregnancyDaysCounter,
            pregnancyWeekNumber,
            user,
            userVendorData,
            userStrollers,
            userStrollersReviews,
        } = profileDetailData;

        const totalProducts = userStrollers?.length ?? 0;
        const profileProducts = this.profileProducts(
            () =>
                // TODO: Rewrite to be more generic when we start to display more products
                userStrollers.slice(0, 3).map((stroller): ProfileProduct => {
                    const photo = stroller.previewPhoto ?? dummyPhoto;
                    const review = userStrollersReviews.find((r) => r.strollerId === stroller.id);

                    return {
                        type: ProfileProductType.STROLLER,
                        id: stroller.id,
                        slug: stroller.slug,
                        name: stroller.name,
                        imageUrl: photo.url250x250,
                        imageWidth: photo.originalWidth,
                        imageHeight: photo.originalHeight,
                        reviewId: review?.id,
                        reviewerScore: review?.score,
                        reviewStatus: review?.status,
                    };
                }),
            userStrollers,
            user,
            userStrollersReviews,
        );

        return (
            <div className={styles.MoreAboutMe}>
                {userVendorData && (
                    <AboutProMe
                        isMobile={isMobile}
                        profileDetailData={profileDetailData}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                    />
                )}

                {(isBlogsAdmin || isOwner || profileProducts?.length > 0) && (
                    <div className={styles.MoreAboutMe__products}>
                        {profileProducts.length > 0 && (
                            <h2 className={styles.MoreAboutMe__products__title}>
                                {MORE_ABOUT_ME_MY_PRODUCTS} ({totalProducts})
                            </h2>
                        )}

                        <div className={styles.MoreAboutMe__products__products}>
                            {profileProducts.length ? (
                                profileProducts.map((p) =>
                                    p.type === ProfileProductType.STROLLER ? (
                                        <MyStrollerProduct
                                            slug={p.slug}
                                            name={p.name}
                                            imageUrl={p.imageUrl}
                                            imageWidth={p.imageWidth}
                                            imageHeight={p.imageHeight}
                                            reviewId={p.reviewId}
                                            reviewerScore={p.reviewerScore}
                                            reviewStatus={p.reviewStatus}
                                            id={`stroller-${p.id}`}
                                            key={p.id}
                                            className={
                                                styles.MoreAboutMe__products__products__product
                                            }
                                            canReview={isOwner}
                                            pathname={location.pathname}
                                        />
                                    ) : null,
                                )
                            ) : (
                                <AddProductsBanner />
                            )}
                        </div>

                        <div className={styles.MoreAboutMe__products__action}>
                            <Btn
                                type={BtnType.Blue}
                                link={url(blogsProfileProductsUrl, { username: user.username })}
                                label={
                                    profileProducts.length === 0
                                        ? MORE_ABOUT_ME_MY_PRODUCTS_ADD_FIRST_PRODUCT
                                        : MORE_ABOUT_ME_MY_PRODUCTS_SHOW
                                }
                                icon={profileProducts.length === 0 ? faPlusCircle : faFolders}
                            />
                        </div>
                    </div>
                )}

                {(isOwner || isBlogsAdmin) && following.count > 0 && (
                    <AvatarImgGrid
                        id="following"
                        countAll={following.count}
                        expanded={following.expanded}
                        hasMore={following.hasMore}
                        header={FAVORITE_USERS}
                        isMobile={isMobile}
                        loadingState={LoadingState.SUCCESS}
                        maxLinesCountIfCollapsed={2}
                        users={following.users}
                        onExpand={this.onExpandAvatarGridCache(
                            () => {
                                onExpandAvatarGrid(AvatarGridType.FOLLOWING, user.username);
                            },
                            user,
                            AvatarGridType.FOLLOWING,
                        )}
                    />
                )}

                {followers.count > 0 && (
                    <AvatarImgGrid
                        id="followers"
                        countAll={followers.count}
                        expanded={followers.expanded}
                        hasMore={followers.hasMore}
                        header={HAVE_ME_AS_FAVORITE}
                        isMobile={isMobile}
                        loadingState={LoadingState.SUCCESS}
                        maxLinesCountIfCollapsed={2}
                        users={followers.users}
                        onExpand={this.onExpandAvatarGridCache(
                            () => {
                                onExpandAvatarGrid(AvatarGridType.FOLLOWERS, user.username);
                            },
                            user,
                            AvatarGridType.FOLLOWERS,
                        )}
                    />
                )}

                {memberOfGroups.count > 0 && (
                    <UserGroupsList
                        id="groups"
                        countAll={memberOfGroups.count}
                        hasMore={memberOfGroups.hasMore}
                        header={I_AM_MEMBER_OF_GROUPS}
                        loadingState={LoadingState.SUCCESS}
                        groups={memberOfGroups.groups}
                        onExpand={this.onExpandGroupListCache(() => {
                            onExpandGroupList(user.username);
                        }, user)}
                    />
                )}
            </div>
        );
    }
}
